import {endOfDay, startOfDay} from 'date-fns';
import {useEffect, useRef} from 'react';

import {logHomeAgendaLoaded} from './dashboardAnalytics';
import {AgendaItem, DashboardState} from './dashboardModel';

const MINUTE_MS = 60_000;

/** Today's timed personal events, plus how soon the next one starts, so we can
 * size the audience with a busy day or a meeting coming up. */
const todayStats = (agenda: AgendaItem[], now: number) => {
  const dayStart = startOfDay(now).getTime();
  const dayEnd = endOfDay(now).getTime();
  const today = agenda.filter(
    (item) =>
      item.source === 'personal' && !item.allDay && item.start >= dayStart && item.start <= dayEnd
  );
  const nextStart = Math.min(...today.map((item) => item.start).filter((start) => start > now));
  return {
    eventsToday: today.length,
    externalToday: today.filter((item) => item.hasExternalGuests).length,
    nextEventInMin: Number.isFinite(nextStart)
      ? Math.round((nextStart - now) / MINUTE_MS)
      : undefined,
  };
};

/**
 * Logs HOME_AGENDA_LOADED once per visit, when the agenda query first settles,
 * so we can tell how many home visitors see their own calendar rather than
 * only company events.
 */
export const useHomeAgendaLoadedLog = ({
  visitId,
  state,
  tileShown,
}: {
  visitId: string;
  state: DashboardState;
  tileShown: boolean;
}) => {
  const loggedRef = useRef(false);
  const {agendaLoading, agenda, agendaCalendar} = state;

  useEffect(() => {
    if (loggedRef.current || agendaLoading !== false) return;
    loggedRef.current = true;
    const personalCount = agenda.filter((item) => item.source === 'personal').length;
    logHomeAgendaLoaded({
      visitId,
      personalCount,
      companyCount: agenda.length - personalCount,
      ...todayStats(agenda, Date.now()),
      calendar: agendaCalendar ?? 'unknown',
      tileShown,
    });
  }, [visitId, agendaLoading, agenda, agendaCalendar, tileShown]);
};
